/**
 * TransactionsMap Hook
 *
 * Renders Google Maps with transaction markers and handles bidirectional hover highlighting.
 * Modeled after ClientPortalToursMap hook.
 */
export const ClientPortalTransactionsMap = {
  async mounted() {
    this.markersById = new Map()
    this.markers = []
    this._deals = null
    this.setupHighlightListeners()
    this.handleEvent("update-map-deals", ({ deals }) => this.updateDeals(deals))
    await this.renderMap()
    this.pushEvent("transactions_map_ready")
  },

  async updateDeals(deals) {
    this._deals = deals
    await this.renderMap()
  },

  setupHighlightListeners() {
    const hook = this

    this.highlightHandler = (event) => {
      const dealId = String(event.detail.dealId)
      hook.highlightMarker(dealId)
    }

    this.unhighlightHandler = () => {
      hook.resetAllMarkers()
    }

    window.addEventListener(
      "transactions-map:highlight-deal",
      this.highlightHandler,
    )
    window.addEventListener(
      "transactions-map:unhighlight-deal",
      this.unhighlightHandler,
    )
  },

  highlightMarker(dealId) {
    this.removePulseOverlay()
    this.markersById.forEach((data, id) => {
      if (id === dealId) {
        data.marker.setIcon(data.highlightedIcon)
        data.marker.setZIndex(1000)
        this.addPulseOverlay(data.marker, data.color)
      } else {
        data.marker.setIcon(data.dimmedIcon)
        data.marker.setZIndex(data.defaultZIndex)
      }
    })
  },

  resetAllMarkers() {
    this.removePulseOverlay()
    this.markersById.forEach((data) => {
      data.marker.setIcon(data.defaultIcon)
      data.marker.setZIndex(data.defaultZIndex)
    })
  },

  addPulseOverlay(marker, color) {
    if (!this.map) return
    const overlay = new google.maps.OverlayView()
    overlay.onAdd = function () {
      const div = document.createElement("div")
      div.style.position = "absolute"
      div.style.pointerEvents = "none"
      div.style.zIndex = "999"
      div.innerHTML = `
        <div style="position:relative;width:120px;height:120px;">
          <div style="
            position:absolute;inset:0;
            border-radius:50%;
            background:${color};
            opacity:0;
            animation: pulse-glow 2s ease-out infinite;
          "></div>
          <div style="
            position:absolute;inset:0;
            border-radius:50%;
            background:${color};
            opacity:0;
            animation: pulse-glow 2s ease-out 0.7s infinite;
          "></div>
        </div>
      `
      this.div = div
      const panes = this.getPanes()
      panes.overlayLayer.appendChild(div)

      // Inject keyframes if not already present
      if (!document.getElementById("pulse-glow-keyframes")) {
        const style = document.createElement("style")
        style.id = "pulse-glow-keyframes"
        style.textContent = `
          @keyframes pulse-glow {
            0% { transform: scale(0.3); opacity: 0.25; filter: blur(2px); }
            100% { transform: scale(1.2); opacity: 0; filter: blur(12px); }
          }
        `
        document.head.appendChild(style)
      }
    }
    overlay.draw = function () {
      const projection = this.getProjection()
      if (!projection) return
      const pos = projection.fromLatLngToDivPixel(marker.getPosition())
      if (pos && this.div) {
        this.div.style.left = pos.x - 60 + "px"
        this.div.style.top = pos.y - 60 + "px"
      }
    }
    overlay.onRemove = function () {
      if (this.div) {
        this.div.parentNode.removeChild(this.div)
        this.div = null
      }
    }
    overlay.setMap(this.map)
    this.pulseOverlay = overlay
  },

  removePulseOverlay() {
    if (this.pulseOverlay) {
      this.pulseOverlay.setMap(null)
      this.pulseOverlay = null
    }
  },

  async renderMap() {
    // Clear existing markers
    if (this.markers) {
      this.markers.forEach((marker) => marker.setMap(null))
      this.markers = []
    }
    this.markersById.clear()

    const deals = this._deals ?? []
    const t = (key) => window.I18n[key]
    const failedToLoadMap = t("Failed to load map")

    // Show empty US map when no deals
    if (deals.length === 0) {
      try {
        await window.Enaia.loadGoogleMaps()

        const usCenter = { lat: 39.8283, lng: -98.5795 }

        if (this.map) {
          // Reset existing map to US-centered view
          this.map.setCenter(usCenter)
          this.map.setZoom(4)
        } else {
          this.map = new google.maps.Map(this.el, {
            zoom: 4,
            minZoom: 3,
            center: usCenter,
            backgroundColor: "#e8eef1",
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            fullscreenControlOptions: {
              position: google.maps.ControlPosition.RIGHT_TOP,
            },
            zoomControl: true,
            zoomControlOptions: {
              position: google.maps.ControlPosition.RIGHT_CENTER,
            },
            styles: window.Enaia.GoogleMapsStyles.DESATURATED_MAP_STYLES,
          })
        }
      } catch (error) {
        console.error("Error loading Google Maps:", error)
        this.el.innerHTML = `<div class="flex items-center justify-center h-full text-red-500 text-[length:0.875rem]">${failedToLoadMap}</div>`
      }
      return
    }

    try {
      await window.Enaia.loadGoogleMaps()
      const hook = this

      if (!this.map) {
        this.map = new google.maps.Map(this.el, {
          zoom: 10,
          minZoom: 3,
          center: {
            lat: parseFloat(deals[0].lat),
            lng: parseFloat(deals[0].lng),
          },
          backgroundColor: "#e8eef1",
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER,
          },
          styles: window.Enaia.GoogleMapsStyles.DESATURATED_MAP_STYLES,
        })
      }

      const bounds = new google.maps.LatLngBounds()

      // Building pin marker SVG (from Figma)
      const buildingIconPath =
        "M11.75 10C11.5511 10 11.3603 10.079 11.2197 10.2197C11.079 10.3603 11 10.5511 11 10.75C11 10.9489 11.079 11.1397 11.2197 11.2803C11.3603 11.421 11.5511 11.5 11.75 11.5H12V20.5H11.75C11.5511 20.5 11.3603 20.579 11.2197 20.7197C11.079 20.8603 11 21.0511 11 21.25C11 21.4489 11.079 21.6397 11.2197 21.7803C11.3603 21.921 11.5511 22 11.75 22H14C14.1326 22 14.2598 21.9473 14.3536 21.8536C14.4473 21.7598 14.5 21.6326 14.5 21.5V18.5C14.5 18.3674 14.5527 18.2402 14.6464 18.1464C14.7402 18.0527 14.8674 18 15 18H17C17.1326 18 17.2598 18.0527 17.3536 18.1464C17.4473 18.2402 17.5 18.3674 17.5 18.5V21.5C17.5 21.6326 17.5527 21.7598 17.6464 21.8536C17.7402 21.9473 17.8674 22 18 22H20.25C20.4489 22 20.6397 21.921 20.7803 21.7803C20.921 21.6397 21 21.4489 21 21.25C21 21.0511 20.921 20.8603 20.7803 20.7197C20.6397 20.579 20.4489 20.5 20.25 20.5H20V11.5H20.25C20.4489 11.5 20.6397 11.421 20.7803 11.2803C20.921 11.1397 21 10.9489 21 10.75C21 10.5511 20.921 10.3603 20.7803 10.2197C20.6397 10.079 20.4489 10 20.25 10H11.75ZM14.5 12C14.3674 12 14.2402 12.0527 14.1464 12.1464C14.0527 12.2402 14 12.3674 14 12.5V13C14 13.1326 14.0527 13.2598 14.1464 13.3536C14.2402 13.4473 14.3674 13.5 14.5 13.5H15C15.1326 13.5 15.2598 13.4473 15.3536 13.3536C15.4473 13.2598 15.5 13.1326 15.5 13V12.5C15.5 12.3674 15.4473 12.2402 15.3536 12.1464C15.2598 12.0527 15.1326 12 15 12H14.5ZM14 15C14 14.8674 14.0527 14.7402 14.1464 14.6464C14.2402 14.5527 14.3674 14.5 14.5 14.5H15C15.1326 14.5 15.2598 14.5527 15.3536 14.6464C15.4473 14.7402 15.5 14.8674 15.5 15V15.5C15.5 15.6326 15.4473 15.7598 15.3536 15.8536C15.2598 15.9473 15.1326 16 15 16H14.5C14.3674 16 14.2402 15.9473 14.1464 15.8536C14.0527 15.7598 14 15.6326 14 15.5V15ZM17 12C16.8674 12 16.7402 12.0527 16.6464 12.1464C16.5527 12.2402 16.5 12.3674 16.5 12.5V13C16.5 13.1326 16.5527 13.2598 16.6464 13.3536C16.7402 13.4473 16.8674 13.5 17 13.5H17.5C17.6326 13.5 17.7598 13.4473 17.8536 13.3536C17.9473 13.2598 18 13.1326 18 13V12.5C18 12.3674 17.9473 12.2402 17.8536 12.1464C17.7598 12.0527 17.6326 12 17.5 12H17ZM16.5 15C16.5 14.8674 16.5527 14.7402 16.6464 14.6464C16.7402 14.5527 16.8674 14.5 17 14.5H17.5C17.6326 14.5 17.7598 14.5527 17.8536 14.6464C17.9473 14.7402 18 14.8674 18 15V15.5C18 15.6326 17.9473 15.7598 17.8536 15.8536C17.7598 15.9473 17.6326 16 17.5 16H17C16.8674 16 16.7402 15.9473 16.6464 15.8536C16.5527 15.7598 16.5 15.6326 16.5 15.5V15Z"

      function makePinSvg(color, opacity, size) {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="15" fill="white" stroke="${color}" stroke-width="2" opacity="${opacity}"/><path fill-rule="evenodd" clip-rule="evenodd" d="${buildingIconPath}" fill="${color}" opacity="${opacity}"/></svg>`
        return {
          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
          scaledSize: new google.maps.Size(size, size),
          anchor: new google.maps.Point(size / 2, size / 2),
        }
      }

      deals.forEach((deal, idx) => {
        const position = {
          lat: parseFloat(deal.lat),
          lng: parseFloat(deal.lng),
        }

        // Color based on deal stage
        const markerColor = deal.stage === "dead" ? "#9ca3af" : "#0086E6"
        const defaultZIndex = idx + 1

        const defaultIcon = makePinSvg(markerColor, 1, 32)
        const highlightedIcon = makePinSvg(markerColor, 1, 40)
        const dimmedIcon = makePinSvg(markerColor, 0.3, 32)

        const marker = new google.maps.Marker({
          position,
          map: this.map,
          title: `${deal.name}\n${deal.city_name}`,
          icon: defaultIcon,
          zIndex: defaultZIndex,
        })

        this.markers.push(marker)
        bounds.extend(position)

        const dealIdStr = String(deal.id)

        hook.markersById.set(dealIdStr, {
          marker,
          defaultIcon,
          highlightedIcon,
          dimmedIcon,
          defaultZIndex,
          color: markerColor,
        })

        marker.addListener("click", () => {
          window.location.href = deal.url
        })

        marker.addListener("mouseover", () => {
          hook.highlightMarker(dealIdStr)
          window.dispatchEvent(
            new CustomEvent("transactions-list:highlight-deal", {
              detail: { dealId: deal.id },
            }),
          )
        })

        marker.addListener("mouseout", () => {
          hook.resetAllMarkers()
          window.dispatchEvent(
            new CustomEvent("transactions-list:unhighlight-deal", {
              detail: { dealId: deal.id },
            }),
          )
        })
      })

      if (deals.length >= 1) {
        this.map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
        const listener = google.maps.event.addListener(this.map, "idle", () => {
          if (this.map.getZoom() > 12) this.map.setZoom(12)
          google.maps.event.removeListener(listener)
        })
      }
    } catch (error) {
      console.error("Error loading Google Maps:", error)
      this.el.innerHTML = `<div class="flex items-center justify-center h-full text-red-500 text-[length:0.875rem]">${failedToLoadMap}</div>`
    }
  },

  destroyed() {
    this.removePulseOverlay()
    if (this.highlightHandler) {
      window.removeEventListener(
        "transactions-map:highlight-deal",
        this.highlightHandler,
      )
    }
    if (this.unhighlightHandler) {
      window.removeEventListener(
        "transactions-map:unhighlight-deal",
        this.unhighlightHandler,
      )
    }
  },
}
